import {
  Checkbox,
  Menu,
  MenuButton,
  MenuPopover,
  MenuTrigger,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowDownload20Regular,
  ArrowSortDown20Regular,
  ArrowSortUp20Regular,
  Checkmark20Regular,
  Dismiss20Regular,
  MoreVertical24Regular,
} from "@fluentui/react-icons";
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import { format } from "date-fns";
import { ja } from "date-fns/locale/ja";
import React, {
  ReactElement,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";

import { Sheet } from "../../../domain/Sheet";
import { Version } from "../../../domain/Version";
import { View } from "../../../domain/View";
import { parseDateTimeOrISODateTime } from "../../../utils/dateFnsHelper";
import styles from "../index.module.css";

import { DATETIME_FORMAT, DATE_FORMAT } from "@/config/date";
import { appViewMasterPrivacySettings } from "@/domain/AppViewMasters";
import { organizationKindList } from "@/domain/Organization";
import { PermissionsAction } from "@/domain/roles";
import { translation } from "@/i18n";
import { nsGrid } from "@/i18n/locales/grid";
import { SortOption } from "../types";
import selectionBarStyles from "./selectionBar.module.css";

export type ContextMenuItem = {
  key: string;
  text: string;
  onClick?:
    | (() => void)
    | ((
        e:
          | React.MouseEvent<HTMLElement, MouseEvent>
          | React.KeyboardEvent<HTMLElement>
          | undefined
      ) => void);
  iconProps?: { iconName: string; style?: React.CSSProperties };
  style?: React.CSSProperties;
  disabled?: boolean;
  hidden?: boolean;
};

// components/GridTable/index.module.css内で設定されたcontextItems用の幅
const RIGHTEND_CELL_WIDTH = 90;
const CHECKBOX_CELL_WIDTH = 50;

type GridTableRow = {
  id: string;
  rowCells: React.ReactNode[];
  values?: (string | number | null)[];
  to?: string;
  contextMenuItems?: ReactElement;
  contextMenuDisabled?: boolean;
  onClick?: () => void;
};

type GridColumn = {
  key: string;
  width: number;
  header: React.ReactNode;
  renderCell: (rowIndex: number) => React.ReactNode;
  className?: string;
  sortableIndex?: number;
  getCellProps?: (
    rowIndex: number
  ) => Pick<React.HTMLAttributes<HTMLDivElement>, "onClick" | "onMouseDown">;
};

type FixedColumnProps = {
  className?: string;
  style?: React.CSSProperties;
};

export const renderDescriptionCell = (text?: string | null) => {
  return (
    <Tooltip
      content={{
        children: text ?? "",
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
        },
      }}
      relationship="description"
    >
      <div className={styles.descriptionCell}>{text ?? ""}</div>
    </Tooltip>
  );
};

const useTranslation = translation.use(nsGrid);

export const renderNameWithId = (title: string, id: string) => {
  return (
    <div className={styles.doubleRows}>
      <div>{title}</div>
      <div className={styles.itemId}>ID : {id}</div>
    </div>
  );
};

export const renderVersionPublishedByOperation = ({
  operationText,
  publishedAt,
}: {
  operationText: string;
  publishedAt: Version["publishedAt"];
}) => {
  const parsedDate = parseDateTimeOrISODateTime(publishedAt);
  if (!parsedDate) return null;
  const formattedDateText = format(parsedDate, DATETIME_FORMAT, {
    locale: ja,
  });
  return (
    <div className={styles.doubleRows}>
      <div> {formattedDateText} </div>
      <div className={styles.doubleRowsUnder}>{operationText}</div>
    </div>
  );
};

export const renderOptimizeStatus = ({
  color,
  optimizeStatusText,
  Icon,
  optimizeFinishedAt,
}: {
  color: string;
  optimizeStatusText: string;
  Icon: React.ComponentType<{ className?: string }>;
  optimizeFinishedAt: Version["optimizeFinishedAt"];
  versionId: Version["id"];
}) => {
  const parsed = optimizeFinishedAt
    ? parseDateTimeOrISODateTime(optimizeFinishedAt)
    : null;
  const formattedDateText = parsed
    ? format(parsed, DATETIME_FORMAT, {
        locale: ja,
      })
    : "-";
  return (
    <div className={styles.statusWrapper}>
      <div className={styles.doubleRows}>
        <div
          style={{
            color,
          }}
          className={styles.statusText}
        >
          <Icon className={styles.statusIcon} />
          <div>{optimizeStatusText}</div>
        </div>
        <div>{formattedDateText}</div>
      </div>
    </div>
  );
};

export const GridTableFontIconButton: React.FC<{
  text: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  iconName: string;
  disabled: boolean;
}> = ({ text, onClick, iconName, disabled }) => {
  return (
    <div
      className={classNames(styles.fontIconButton, {
        [styles.disabledButton]: disabled,
      })}
      onClick={(e) => {
        e.preventDefault();
        if (disabled) return;
        onClick && onClick(e);
      }}
    >
      <ArrowDownload20Regular className={styles.downloadIcon} />
      <div>{text}</div>
    </div>
  );
};

export const renderGridTableDateTimeCell = (
  dateTime: Sheet["updatedAt"] | View["updatedAt"] | Version["lastModifiedAt"]
) => {
  const parsed = parseDateTimeOrISODateTime(dateTime);
  if (!parsed) return null;
  return (
    <div className={styles.doubleRows}>
      <div>
        {format(parsed, DATE_FORMAT, {
          locale: ja,
        })}
      </div>
      <div>
        {format(parsed, "HH:mm", {
          locale: ja,
        })}
      </div>
    </div>
  );
};

export const EnablementStatusCell: React.FC<{ isDisabled: boolean }> = ({
  isDisabled,
}) => {
  const { t } = useTranslation();
  const Icon = isDisabled ? Dismiss20Regular : Checkmark20Regular;
  const label = isDisabled ? t.grid.disabled() : t.grid.enabled();

  return (
    <div
      className={styles.statusText}
      style={{
        color: isDisabled
          ? "var(--figma-color-gray60)"
          : "var(--figma-color-success-green)",
      }}
    >
      <Icon className={styles.statusIcon} />
      <div>{label}</div>
    </div>
  );
};

export const renderPrivacySetting = (
  privacySetting?: (typeof appViewMasterPrivacySettings)[number]
) => {
  if (!privacySetting) return null;
  return (
    <div className={classNames(styles.badge, styles[privacySetting])}>
      {privacySetting}
    </div>
  );
};

export const renderOrgKind = (
  orgKind: (typeof organizationKindList)[number]
) => {
  let orgStyle;
  if (orgKind.startsWith("optium")) {
    orgStyle = styles.optium;
  } else if (orgKind.startsWith("planium")) {
    orgStyle = styles.planium;
  } else if (orgKind.startsWith("dldx")) {
    orgStyle = styles.dldx;
  } else {
    orgStyle = styles.others;
  }
  return <div className={classNames(styles.badge, orgStyle)}>{orgKind}</div>;
};

export const OrgNumCell: React.FC<{
  orgNum: number;
  maxOrgNum: number | null;
}> = ({ orgNum, maxOrgNum }) => {
  const { t } = useTranslation();
  if (!maxOrgNum)
    return <div>{t.grid.memberCountUnlimited({ count: orgNum })}</div>;
  return <div>{t.grid.memberCount({ count: orgNum, max: maxOrgNum })}</div>;
};

export const renderPermissionsAction = (action: PermissionsAction) => {
  return (
    <div className={classNames(styles.badge, styles[action])}>{action}</div>
  );
};

export const renderMultipleNamesCell = <
  T extends { id: string; displayName?: string; title?: string },
>(
  arr: T[]
) => {
  if (arr.length === 0) {
    return null;
  }
  if (arr.length === 1) {
    return <div>{arr[0].displayName || arr[0].title || ""}</div>;
  }
  return (
    <Tooltip
      content={{
        children: arr
          .map(({ displayName, title }) => displayName || title || "")
          .join(", "),
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
        },
      }}
      relationship="description"
    >
      <div>{`${arr[0].displayName || arr[0].title || ""} ...`}</div>
    </Tooltip>
  );
};

/**
 * @description グリッドテーブルを作成するテンプレート.各行はtoを指定すればLinkによってURL遷移可能
 * ヘッダセルをクリックで並び替えをしたいときは、tbodyRowCellsのvaluesに並び替えのための値を入れた上で、optionsでsortOptionとsetSortOptionを指定する
 * NOTE: theadCellsとtbodyRowsCellsの長さが同じでないと描画が変になる
 */
export const GridTableTemplate: React.FC<{
  theadCells: string[];
  tbodyRowsCells: GridTableRow[];
  cellsWidth: number[];
  // 列ごとに付けるクラス。checkbox 列と同じ見た目にしたい列などに使う
  cellsClassName?: (string | undefined)[];
  headerElement?: ReactElement;
  checkedIds?: string[];
  setCheckedIds?: React.Dispatch<React.SetStateAction<string[]>>;
  enableContextMenuColumn?: boolean;
  fitContentHeight?: boolean;
  fixedColumnCounts?: {
    // 左端から固定する列数。checkbox列がある場合はそれも含めた見た目上の列数を指定する
    left?: number;
    // 右端から固定する列数。その他列がある場合はそれも含めた見た目上の列数を指定する
    right?: number;
  };
  options?: {
    sort: {
      sortOption?: SortOption | null;
      unsortableColumnIndices?: number[];
      setSortOption?: React.Dispatch<React.SetStateAction<SortOption | null>>;
    };
  };
}> = ({
  theadCells,
  tbodyRowsCells,
  cellsWidth,
  cellsClassName,
  headerElement,
  checkedIds,
  setCheckedIds,
  enableContextMenuColumn,
  fitContentHeight,
  fixedColumnCounts,
  options,
}) => {
  const { t } = useTranslation();
  const { sortOption, setSortOption, unsortableColumnIndices } =
    options?.sort ?? {
      sortOption: null,
      setSortOption: undefined,
    };

  const rows = useMemo(
    () =>
      sortOption
        ? [...tbodyRowsCells].sort((a, b) => {
            const aVal = a.values?.at(sortOption.index) ?? "";
            const bVal = b.values?.at(sortOption.index) ?? "";
            const comparison =
              typeof aVal === "number" && typeof bVal === "number"
                ? aVal - bVal
                : String(aVal).localeCompare(String(bVal));
            return sortOption.asc ? comparison : -comparison;
          })
        : [...tbodyRowsCells],
    [tbodyRowsCells, sortOption]
  );

  const [showContextualMenuId, setShowContextualMenuId] = useState("");
  const enableContextMenuItems = enableContextMenuColumn ?? false;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);

  const wrapperOffsetRef = useRef(0);
  useLayoutEffect(() => {
    wrapperOffsetRef.current = wrapperRef.current?.offsetTop ?? 0;
  }, []);
  const handleSort = useCallback(
    (index: number) => {
      setSortOption?.((prev) => {
        if (prev?.index === index) {
          return {
            asc: !prev.asc,
            index,
          };
        }
        return {
          asc: true,
          index,
        };
      });
    },
    [setSortOption]
  );
  const toggleCheckedId = useCallback(
    (id: string) => {
      setCheckedIds?.((previous) =>
        previous.includes(id)
          ? previous.filter((checkedId) => checkedId !== id)
          : [...previous, id]
      );
    },
    [setCheckedIds]
  );

  const columns = useMemo<GridColumn[]>(() => {
    const result: GridColumn[] = [];

    if (setCheckedIds && checkedIds) {
      result.push({
        key: "checkbox",
        width: CHECKBOX_CELL_WIDTH,
        header: (
          <Checkbox
            size="large"
            checked={(() => {
              if (!checkedIds.length) {
                return false;
              }
              const checkedRowCount = rows.filter(({ id }) =>
                checkedIds.includes(id)
              ).length;
              if (checkedRowCount === rows.length) {
                return true;
              }
              if (checkedRowCount > 0) {
                return "mixed";
              }
              return false;
            })()}
            onClick={() => {
              const noneChecked = rows.every(({ id }) => {
                return !checkedIds.includes(id);
              });
              if (!noneChecked) {
                setCheckedIds((beforeCheckedIds) => {
                  return beforeCheckedIds.filter((checkedId) => {
                    return !rows.find(({ id }) => id === checkedId);
                  });
                });
              } else {
                setCheckedIds((beforeCheckedIds) => {
                  const newCheckedIds = rows
                    .map(({ id }) => id)
                    .filter((id) => !beforeCheckedIds.includes(id));
                  return [...beforeCheckedIds, ...newCheckedIds];
                });
              }
            }}
          />
        ),
        className: styles.checkbox,
        renderCell: (rowIndex) => {
          const { id } = rows[rowIndex];
          return (
            <Checkbox
              size="large"
              checked={!!checkedIds.length && checkedIds.includes(id)}
              onClick={(event) => event.stopPropagation()}
              onChange={() => toggleCheckedId(id)}
            />
          );
        },
        getCellProps: (rowIndex) => {
          const { id } = rows[rowIndex];
          return {
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleCheckedId(id);
            },
          };
        },
      });
    }

    theadCells.forEach((cell, index) => {
      result.push({
        key: `data-${index}`,
        width: cellsWidth[index],
        className: cellsClassName?.[index],
        header: (
          <>
            <div>{cell}</div>
            {sortOption?.index === index && (
              <div>
                {sortOption.asc ? (
                  <ArrowSortUp20Regular />
                ) : (
                  <ArrowSortDown20Regular />
                )}
              </div>
            )}
          </>
        ),
        sortableIndex:
          setSortOption && !unsortableColumnIndices?.includes(index)
            ? index
            : undefined,
        renderCell: (rowIndex) => rows[rowIndex].rowCells[index],
      });
    });

    if (enableContextMenuItems) {
      result.push({
        key: "context-menu",
        width: RIGHTEND_CELL_WIDTH,
        header: t.grid.otherColumn(),
        className: styles.contextItems,
        renderCell: (rowIndex) => {
          const { id, contextMenuItems, contextMenuDisabled } = rows[rowIndex];
          if (contextMenuDisabled) {
            return (
              <MenuButton
                id={"listmore__" + id}
                appearance="transparent"
                icon={<MoreVertical24Regular />}
                disabled
              />
            );
          }
          if (!contextMenuItems) return null;
          return (
            <Menu
              positioning={{
                offset: ({ targetRect }) => 16 - targetRect.height / 2,
              }}
              onOpenChange={(_, data) => {
                if (data.open) {
                  setShowContextualMenuId(id);
                } else {
                  setShowContextualMenuId("");
                }
              }}
            >
              <MenuTrigger disableButtonEnhancement>
                {(triggerProps) => (
                  <MenuButton
                    {...triggerProps}
                    id={"listmore__" + id}
                    appearance="transparent"
                    aria-label={t.grid.otherColumn()}
                    icon={<MoreVertical24Regular />}
                    shape="square"
                    style={{
                      alignSelf: "stretch",
                      width: "100%",
                      minWidth: "100%",
                    }}
                  />
                )}
              </MenuTrigger>
              <MenuPopover
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {contextMenuItems}
              </MenuPopover>
            </Menu>
          );
        },
        getCellProps: () => ({
          onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
          },
        }),
      });
    }

    return result;
  }, [
    cellsClassName,
    cellsWidth,
    checkedIds,
    enableContextMenuItems,
    rows,
    setCheckedIds,
    setSortOption,
    sortOption,
    theadCells,
    unsortableColumnIndices,
    toggleCheckedId,
  ]);
  const columnCount = columns.length;
  const fixedLeftCount = Math.min(
    columnCount,
    Math.max(0, fixedColumnCounts?.left ?? 0)
  );
  const fixedRightCount = Math.min(
    columnCount - fixedLeftCount,
    Math.max(0, fixedColumnCounts?.right ?? 0)
  );
  const isFixedColumnEnabled = fixedLeftCount > 0 || fixedRightCount > 0;
  const totalColumnWidth = useMemo(
    () => columns.reduce((sum, column) => sum + column.width, 0),
    [columns]
  );
  const columnLeftOffsets = useMemo(() => {
    let offset = 0;
    return columns.map((column) => {
      const currentOffset = offset;
      offset += column.width;
      return currentOffset;
    });
  }, [columns]);
  const columnRightOffsets = useMemo(() => {
    let offset = 0;
    const offsets = Array.from({ length: columns.length }, () => 0);
    for (let index = columns.length - 1; index >= 0; index -= 1) {
      offsets[index] = offset;
      offset += columns[index].width;
    }
    return offsets;
  }, [columns]);
  const getFixedColumnProps = useCallback(
    (columnIndex: number, isHeader = false): FixedColumnProps => {
      const isLeftFixed = columnIndex < fixedLeftCount;
      const isRightFixed = columnIndex >= columnCount - fixedRightCount;

      if (!isLeftFixed && !isRightFixed) return {};

      return {
        className: classNames(
          styles.fixedCell,
          isHeader && styles.fixedCellHeader
        ),
        style: isLeftFixed
          ? { left: columnLeftOffsets[columnIndex] }
          : { right: columnRightOffsets[columnIndex] },
      };
    },
    [
      columnCount,
      columnLeftOffsets,
      columnRightOffsets,
      fixedLeftCount,
      fixedRightCount,
    ]
  );
  const fixedColumnIndexes = useMemo(() => {
    const indexes = new Set<number>();
    for (let index = 0; index < fixedLeftCount; index += 1) {
      indexes.add(index);
    }
    for (
      let index = columnCount - fixedRightCount;
      index < columnCount;
      index += 1
    ) {
      indexes.add(index);
    }
    return indexes;
  }, [columnCount, fixedLeftCount, fixedRightCount]);
  const getColumnWidth = useCallback(
    (index: number) => columns[index].width,
    [columns]
  );

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 100,
    overscan: 5,
    scrollMargin: wrapperOffsetRef.current,
    paddingStart: 50, // .rowHeader:height
  });
  const rowItems = rowVirtualizer.getVirtualItems();

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: columnCount,
    getScrollElement: () => parentRef.current,
    estimateSize: getColumnWidth,
    overscan: 5,
    rangeExtractor: (range) => {
      const virtualizedIndexes = defaultRangeExtractor(range);
      return Array.from(
        new Set([...fixedColumnIndexes, ...virtualizedIndexes])
      ).sort((a, b) => a - b);
    },
  });
  const columnItems = columnVirtualizer.getVirtualItems();
  const renderedColumnIndexes = useMemo(
    () => columnItems.map(({ index }) => index),
    [columnItems]
  );
  const renderColumnGap = useCallback(
    (width: number, key: string) => (
      <div key={key} className={styles.columnVirtualMargin} style={{ width }} />
    ),
    []
  );
  // 固定列境界線は幅0のsticky要素(edgeBorder)が描き、境界に隣接するedgeBorderCoverが
  // 通常フロー上の境界位置からコンテンツと一緒に動く。端ではcoverが境界線を覆い、
  // スクロール時は固定セルの背面を通過する。
  const renderVisibleColumns = useCallback(
    (
      renderColumn: (
        columnIndex: number,
        column: GridColumn,
        endCellClassName: string
      ) => React.ReactNode
    ) => {
      const renderedColumns: React.ReactNode[] = [];
      let renderedEnd = 0;

      renderedColumnIndexes.forEach((columnIndex, index) => {
        const column = columns[columnIndex];
        const columnStart = columnLeftOffsets[columnIndex];
        const gapWidth = columnStart - renderedEnd;

        if (index === 0 || gapWidth > 0) {
          renderedColumns.push(renderColumnGap(gapWidth, `gap-${columnIndex}`));
        }

        if (
          fixedRightCount > 0 &&
          columnIndex === columnCount - fixedRightCount
        ) {
          renderedColumns.push(
            <span
              key="edge-border-right"
              className={classNames(styles.edgeBorder, styles.edgeBorderRight)}
              style={{
                right: columnRightOffsets[columnIndex - 1] ?? totalColumnWidth,
              }}
            />,
            <span
              key="edge-cover-right"
              className={classNames(
                styles.edgeBorderCover,
                styles.edgeBorderCoverRight
              )}
            />
          );
        }

        renderedColumns.push(
          renderColumn(
            columnIndex,
            column,
            classNames({
              [styles.leftEndCell]: index === 0,
              [styles.rightEndCell]: index === renderedColumnIndexes.length - 1,
            })
          )
        );

        if (fixedLeftCount > 0 && columnIndex === fixedLeftCount - 1) {
          renderedColumns.push(
            <span
              key="edge-border-left"
              className={classNames(styles.edgeBorder, styles.edgeBorderLeft)}
              style={{
                left: columnLeftOffsets[columnIndex + 1] ?? totalColumnWidth,
              }}
            />,
            <span
              key="edge-cover-left"
              className={classNames(
                styles.edgeBorderCover,
                styles.edgeBorderCoverLeft
              )}
            />
          );
        }

        renderedEnd = columnStart + column.width;
      });

      renderedColumns.push(
        renderColumnGap(totalColumnWidth - renderedEnd, "gap-end")
      );

      return renderedColumns;
    },
    [
      columnCount,
      columnLeftOffsets,
      columnRightOffsets,
      columns,
      fixedLeftCount,
      fixedRightCount,
      renderedColumnIndexes,
      renderColumnGap,
      totalColumnWidth,
    ]
  );

  return (
    <>
      {headerElement ? (
        <div
          className={selectionBarStyles.headerContainer}
          data-is-fixed-column={isFixedColumnEnabled ? "true" : undefined}
          style={{
            maxWidth: totalColumnWidth,
          }}
        >
          {headerElement}
        </div>
      ) : null}
      <div
        className={styles.wrapper}
        ref={wrapperRef}
        data-is-fixed-column={isFixedColumnEnabled ? "true" : undefined}
        data-fit-content-height={fitContentHeight ? "true" : undefined}
        style={{
          maxWidth: totalColumnWidth,
          maxHeight: fitContentHeight
            ? undefined
            : rowVirtualizer.getTotalSize(),
        }}
      >
        <div ref={parentRef} className={styles.headerParent}>
          <div
            className={styles.headerTable}
            style={{
              height: rowVirtualizer.getTotalSize(),
              width: totalColumnWidth,
            }}
          >
            {/* 表ヘッダ */}
            <div
              className={classNames(styles.row, {
                [styles.rowHeader]: !headerElement,
                [styles.rowSecondHeader]: headerElement,
              })}
            >
              {renderVisibleColumns((columnIndex, column, endCellClassName) => {
                const fixedColumnProps = getFixedColumnProps(columnIndex, true);

                const commonProps = {
                  className: classNames(
                    column.className,
                    fixedColumnProps.className,
                    endCellClassName
                  ),
                  style: {
                    width: column.width,
                    minWidth: column.width,
                    ...fixedColumnProps.style,
                  },
                };

                return column.sortableIndex !== undefined ? (
                  <button
                    key={column.key}
                    {...commonProps}
                    className={classNames(
                      commonProps.className,
                      styles.headerCellSortable
                    )}
                    onClick={() => {
                      if (column.sortableIndex !== undefined) {
                        handleSort(column.sortableIndex);
                      }
                    }}
                  >
                    {column.header}
                  </button>
                ) : (
                  <div key={column.key} {...commonProps}>
                    {column.header}
                  </div>
                );
              })}
            </div>

            {/* 表ボディ */}
            {rowItems.map((virtualRow) => {
              const { id, to, onClick } = rows[virtualRow.index];
              const rowContent = (
                <div
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    transform: `translateY(${
                      virtualRow.start - rowVirtualizer.options.scrollMargin
                    }px)`,
                    display: "flex",
                  }}
                  className={classNames(styles.row, {
                    [styles.rowBottom]: virtualRow.index === rows.length - 1,
                    [styles.rowActive]:
                      showContextualMenuId === id || checkedIds?.includes(id),
                    [styles.rowClickable]: !!onClick,
                  })}
                  onClick={(e) => {
                    if (!onClick) return;
                    // to(Link)がある場合は遷移を妨げず onClick を副作用として実行。
                    // to がない場合は従来通り onClick が遷移/アクションを担う。
                    if (!to) e.preventDefault();
                    onClick();
                  }}
                >
                  {renderVisibleColumns(
                    (columnIndex, column, endCellClassName) => {
                      const fixedColumnProps = getFixedColumnProps(columnIndex);

                      return (
                        <div
                          key={`${virtualRow.index}-${column.key}`}
                          className={classNames(
                            column.className,
                            fixedColumnProps.className,
                            endCellClassName
                          )}
                          style={{
                            minHeight: virtualRow.size,
                            width: column.width,
                            minWidth: column.width,
                            ...fixedColumnProps.style,
                          }}
                          {...column.getCellProps?.(virtualRow.index)}
                        >
                          {column.renderCell(virtualRow.index)}
                        </div>
                      );
                    }
                  )}
                </div>
              );
              return to ? (
                <Link key={id} to={to}>
                  {rowContent}
                </Link>
              ) : (
                <div key={id}>{rowContent}</div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
